export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-16">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-2">
          Privacy Policy
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mb-8">
          Last updated: {new Date().toLocaleDateString()}
        </p>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 space-y-8">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              1. Introduction
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              Rondanini Publishing Ltd ("we", "our", or "us") operates the learn.rondanini.com website
              (the "Site"). This Privacy Policy explains how we collect, use, disclose, and safeguard
              your information when you visit our Site.
            </p>
          </section>

          {/* Information We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              2. Information We Collect
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Personal Data
                </h3>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                  <li>Name and email address</li>
                  <li>Billing and shipping address</li>
                  <li>Payment information (processed securely via Stripe)</li>
                  <li>Account credentials (username, password)</li>
                  <li>Purchase history and preferences</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Automatic Data
                </h3>
                <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
                  <li>IP address and device information</li>
                  <li>Browser type and operating system</li>
                  <li>Pages visited and time spent on pages</li>
                  <li>Referral source and exit pages</li>
                  <li>Cookie identifiers</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Information */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              3. How We Use Your Information
            </h2>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
              <li>Process and fulfill your orders and transactions</li>
              <li>Send transactional emails (order confirmations, receipts)</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Send marketing communications (with your consent)</li>
              <li>Improve our Site and services through analytics</li>
              <li>Detect and prevent fraud and security issues</li>
              <li>Comply with legal and regulatory obligations</li>
              <li>Personalize your user experience</li>
            </ul>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              4. Cookies and Tracking Technologies
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We use cookies and similar technologies to enhance your experience:
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
              <li><strong>Necessary Cookies:</strong> Session management, security, login authentication</li>
              <li><strong>Analytics Cookies:</strong> Google Analytics for traffic and behavior analysis</li>
              <li><strong>Functional Cookies:</strong> Dark mode preference, saved settings</li>
              <li><strong>Marketing Cookies:</strong> Retargeting ads via Facebook Pixel, Google Ads</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 mt-4">
              You can control cookies through your browser settings and our Cookie Consent banner.
            </p>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              5. Data Sharing and Third Parties
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              We share your information only with:
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
              <li><strong>Stripe:</strong> Payment processing (PCI-DSS compliant)</li>
              <li><strong>Brevo (Sendinblue):</strong> Email delivery for confirmations</li>
              <li><strong>Google Analytics:</strong> Anonymous traffic analysis</li>
              <li><strong>Facebook/Google:</strong> Retargeting advertisements (with consent)</li>
              <li><strong>Law enforcement:</strong> When required by law</li>
            </ul>
          </section>

          {/* Data Security */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              6. Data Security
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              We implement industry-standard security measures to protect your data:
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2 mt-4">
              <li>SSL/TLS encryption for all data in transit</li>
              <li>Password hashing with SHA256 algorithm</li>
              <li>Secure Stripe integration for payment processing</li>
              <li>Regular security audits and monitoring</li>
              <li>Access controls and authentication requirements</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 mt-4">
              However, no security system is impenetrable. We cannot guarantee absolute security.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              7. Your Privacy Rights (GDPR)
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              If you are in the EU, you have the following rights:
            </p>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
              <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
              <li><strong>Right to Rectification:</strong> Correct inaccurate information</li>
              <li><strong>Right to Erasure:</strong> Request deletion of your data</li>
              <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
              <li><strong>Right to Data Portability:</strong> Export your data in a standard format</li>
              <li><strong>Right to Object:</strong> Object to processing of your data</li>
            </ul>
            <p className="text-gray-700 dark:text-gray-300 mt-4">
              To exercise these rights, contact us at: <a href="mailto:privacy@rondanini.com" className="text-navy-600 dark:text-navy-400 hover:underline">
                privacy@rondanini.com
              </a>
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              8. Data Retention
            </h2>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300 space-y-2">
              <li><strong>Account Data:</strong> Retained while your account is active, deleted on request</li>
              <li><strong>Transaction Data:</strong> Retained for 7 years (tax and legal compliance)</li>
              <li><strong>Session Tokens:</strong> Expire after 30 days of inactivity</li>
              <li><strong>Analytics Data:</strong> Retained for 26 months (Google Analytics default)</li>
              <li><strong>Cookie Preferences:</strong> Retained for 365 days</li>
            </ul>
          </section>

          {/* Children's Privacy */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              9. Children's Privacy
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              Our Site is not intended for children under 16 years of age. We do not knowingly collect
              personal information from children. If we become aware that a child has provided us with
              personal information, we will delete it immediately.
            </p>
          </section>

          {/* Contact Us */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              10. Contact Us
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              For questions about this Privacy Policy or your data:
            </p>
            <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-6 mt-4 space-y-2">
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Email:</strong> <a href="mailto:privacy@rondanini.com" className="text-navy-600 dark:text-navy-400 hover:underline">
                  privacy@rondanini.com
                </a>
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Website:</strong> www.rondanini.com
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <strong>Response Time:</strong> We will respond to your inquiries within 30 days
              </p>
            </div>
          </section>

          {/* Policy Changes */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              11. Changes to This Policy
            </h2>
            <p className="text-gray-700 dark:text-gray-300">
              We may update this Privacy Policy periodically. The updated version will be posted on
              this page with a new "Last updated" date. Your continued use of the Site constitutes
              your acceptance of the updated Privacy Policy.
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}
